/**
 * @file alert.tsx
 * @description Inline-alert для error/info/warning/success состояний (phase08.5).
 */

import { cva, type VariantProps } from "class-variance-authority";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
} from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg p-4 text-sm flex gap-3 items-start [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:mt-0.5",
  {
    variants: {
      severity: {
        info: "bg-info/8 text-on-surface [&>svg]:text-info",
        success: "bg-success/10 text-on-surface [&>svg]:text-success",
        warning: "bg-warning/12 text-on-surface [&>svg]:text-warning",
        error: "bg-destructive/10 text-on-surface [&>svg]:text-destructive",
      },
    },
    defaultVariants: { severity: "info" },
  },
);

const SEVERITY_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  error: AlertCircle,
} as const;

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  /** Если задано, иконка наследуется от severity. */
  icon?: React.ReactNode;
}

export function Alert({
  className,
  severity = "info",
  title,
  icon,
  children,
  ...props
}: AlertProps) {
  const Icon = SEVERITY_ICONS[severity ?? "info"];
  return (
    <div role="alert" data-slot="alert" data-severity={severity ?? "info"} className={cn(alertVariants({ severity }), className)} {...props}>
      {icon ?? <Icon />}
      <div className="flex-1 min-w-0">
        {title ? <div className="font-medium mb-0.5">{title}</div> : null}
        {children ? <div data-slot="alert-description" className="text-on-surface-variant">{children}</div> : null}
      </div>
    </div>
  );
}
